import React from 'react';

export interface SplitFirstProductIdsResult {
  /**
   * The original link with firstProductId parameter
   */
  fullLink: string | undefined;
  /**
   * The link without firstProductId parameter for SEO purposes
   */
  linkWithoutFirstProductIds: string | undefined;
}

/**
 * Hook to split firstProductId parameter from a URL link.
 * This is done for SEO purposes to avoid duplicate content issues.
 *
 * @see https://aboutyou.atlassian.net/browse/ASFE-13411
 *
 * @param targetLink - The URL to process
 * @returns An object containing both the full link and the link without firstProductId
 *
 * @example
 * ```tsx
 * const {fullLink, linkWithoutFirstProductIds} = useSplitFirstProductIds(
 *   'https://example.com/path?firstProductId=123&other=param'
 * );
 * // fullLink: 'https://example.com/path?firstProductId=123&other=param'
 * // linkWithoutFirstProductIds: '/path?other=param'
 * ```
 */
export const useSplitFirstProductIds = (
  targetLink: string | undefined,
): SplitFirstProductIdsResult => {
  return React.useMemo(() => {
    if (!targetLink) {
      return {
        linkWithoutFirstProductIds: undefined,
        fullLink: undefined,
      };
    }
    const [pathname, search] = targetLink.split('?');
    if (search === undefined) {
      return {linkWithoutFirstProductIds: targetLink, fullLink: targetLink};
    }
    const searchParams = new URLSearchParams(search);
    searchParams.delete('firstProductId');
    const remainingSearch = searchParams.toString();

    return {
      linkWithoutFirstProductIds: remainingSearch
        ? `${pathname}?${remainingSearch}`
        : pathname,
      fullLink: targetLink,
    };
  }, [targetLink]);
};
